import React, { useState } from "react";
import { Box, TextField, Button, FormHelperText } from "@mui/material";
import { NoteBlockContainer } from "../../note";
import { DynamicTextField } from "../../dynamicTextField";
import { useVolume, useVolumeLoad } from "../../../hook/volume";
import { LoadBoundary } from "../../brand/LoadBoundary";
import { VolumeBlock } from "../../../types/notebook/note/block/volume";
import { volumeName, volumeSize, humanToBytes, bytesToHuman } from "../../../types/volume";
import { useAuthToken } from "../../../hook";
import { provisionVolume } from "../../../api/provisionVolume";

export const VolumeBlockComponent: React.FC<{ block: VolumeBlock }> =
  React.memo(({ block }) => {
    const { setName, setSize } = useVolume(block["volume-id"]);
    const { volume, status, retry } = useVolumeLoad(block);
    const [authToken] = useAuthToken();
    const name = volumeName(volume);
    const savedHuman = bytesToHuman(volumeSize(volume));
    const [draft, setDraft] = useState<string | null>(null);
    const human = draft ?? savedHuman;
    const bytes = humanToBytes(human);
    const valid = human === "" || bytes !== null;
    const dirty = draft !== null && human !== savedHuman;
    return (
      <NoteBlockContainer name="Volume">
        <LoadBoundary
          status={status}
          onRetry={retry}
          label="Loading volume"
          errorMessage="Could not load this volume."
          testId="volume-loading"
        >
        <Box data-testid="volume-block">
          <DynamicTextField data-testid="volume-name" text={name} setText={setName} placeholder="Name" />
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
            <TextField
              data-testid="volume-size"
              value={human}
              onChange={(e) => setDraft(e.target.value)}
              size="small" variant="outlined" error={human !== "" && !valid}
              placeholder="Size (e.g. 10Gi)"
            />
            <Button
              data-testid="volume-size-save"
              variant="outlined"
              disabled={!valid || !dirty || bytes === null}
              onClick={() => {
                setSize(bytes as number);
                provisionVolume(authToken ?? "", block["volume-id"], bytes as number);
                setDraft(null);
              }}
            >Save</Button>
          </Box>
          {human !== "" && !valid && <FormHelperText error>Enter a size like 10Gi / 500Mi</FormHelperText>}
        </Box>
        </LoadBoundary>
      </NoteBlockContainer>
    );
  });
